/**
 * 요청함 — /catalog-entry/requests/:filter (VOCABULARY "요청함 페이지" · hcltech-parts CLAUDE.md V장 "카탈로그 승격 — 요청함 모듈 이름" · 2026-10-14).
 * 카탈로그 메뉴의 첫 화면(A 안). 머리(제목 + 메뉴 탭 줄) 아래 탭 넷(확인 대기 · 요청됨 · 컨펌 · 반려) + 전면 표 + 페이저.
 * 줄의 파트넘버를 누르면 요청 상세가 새 탭(CatalogRequestDetailView). 요청함 한 줄 = catalog_part_pending 한 행.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type { ECatalogRequestFilter } from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { notesPopAt } from '../components/notes-pop';
import { assert } from '../lib/assert';
import { renderCatalogLineTable, renderCloseReport, renderRequestFilters, renderRequestTable } from './catalog-requests/render';
import type { CatalogRequestsData } from './catalog-requests/types';
import { hasTextSelection } from '../lib/selection';

const MENU_HREF = '/catalog-entry/requests';
const QUERY_DEBOUNCE_MS = 300;      // 찾기 — 글자마다 서버를 안 부른다(제품 목록과 같게)

/** URL 의 filter — 셋 밖이면 기본 drafted(CatalogEntryView.toSection 꼴) */
export function toRequestFilter(raw: string): ECatalogRequestFilter {
  const found = api.E_CATALOG_REQUEST_FILTERS.find((f) => f === raw);
  return found === undefined ? 'drafted' : found;
}

const CLOSE_IN_CATALOG_ASK = '옵션 PN 이 카탈로그에 이미 있는 열린 요청을 전부 연결로 닫습니다(컨펌 + product 채움). 카탈로그 행은 안 바뀝니다. 돌릴까요?';

export class CatalogRequestsView extends SolaView<CatalogRequestsData> {
  private queryTimer: ReturnType<typeof setTimeout> | null = null;
  private loadSeq = 0;   // 불러오기 순번 — 늦게 온 옛 응답이 새 결과를 덮지 않게(QA 2026-10-20 #3 응답 경합)

  override events = {
    // 메모 넷이 줄 열기(open)보다 앞 — Sola 는 먼저 맞는 것 하나만 부른다. 창 안을 눌러도 줄 열기로 안 넘어간다(notes-pop)
    click: { '@notes-save': 'handleNotesSave', '@notes-close': 'handleNotesClose', '@notes-pop': 'handleNotesPop', '@notes-toggle': 'handleNotesToggle', '@tab': 'handleTab', '@page': 'handlePage', '@open': 'handleOpen', '@close-in-catalog': 'handleCloseInCatalog', '@close-report-dismiss': 'handleCloseReportDismiss' },
    input: { '@query': 'handleQuery', '@notes-input': 'handleNotesInput' },
    keydown: { '@notes-input': 'handleNotesKey' },   // Esc 닫기
    change: { '@period': 'handlePeriod', '@table': 'handleTable' },   // 전체 탭은 기간 대신 카테고리(2026-10-20)
  };

  constructor(params: { filter: ECatalogRequestFilter }) {
    super();
    this.data = { filter: params.filter, q: '', period: 'all', list: null, lines: null, table: null, offset: 0, loading: true, error: '', notesPop: null, closeReport: null };
  }

  override getTitle(): string {
    return '카탈로그 요청 — HCLTech';
  }

  override onMount(): void {
    void this.load();
  }

  render(): HTMLElement {
    const place = findMenuPlace(MENU_HREF);
    assert(place !== null, `요청함이 메뉴에 없다: ${MENU_HREF} — MENU_GROUPS 카탈로그의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-catalog-requests pv-page">
        <header class="page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
        </header>
        <section class="page-body pv-body cr-body">${this.renderBody()}</section>
      </div>`;
  }

  // ─── 핸들러 — 던지면 Sola 의 onActionError(alert) 가 받는다 ───
  /** 찾기 글 — 글자마다 서버를 안 부른다(300ms). 묶음은 처음부터 */
  handleQuery(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, q: (el as HTMLInputElement).value, offset: 0 };
    if (this.queryTimer !== null) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => { this.queryTimer = null; void this.load(); }, QUERY_DEBOUNCE_MS);
  }

  /** 기간 — 요청날짜가 7 · 30일 안인 줄만(회의 2026-10-15). 묶음은 처음부터 */
  handlePeriod(_e: Event, el: HTMLElement): void {
    const value = (el as HTMLSelectElement).value;
    const period = api.E_REQUEST_PERIODS.find((p) => p === value);
    assert(period !== undefined, `기간 셀렉트의 값은 E_REQUEST_PERIODS 중 하나 — renderRequestFilters 가 그렸다: ${value}`);
    this.data = { ...this.data, period, offset: 0 };
    void this.load();
  }

  /** 전체 탭의 표 필터 — '' 은 전부 */
  handleTable(_e: Event, el: HTMLElement): void {
    const value = (el as HTMLSelectElement).value;
    const table = api.E_CATALOG_TABLES.find((t) => t === value);
    this.data = { ...this.data, table: table === undefined ? null : table, offset: 0 };
    void this.load();
  }

  handleTab(_e: Event, el: HTMLElement): void {
    const filter = el.dataset.filter;
    assert(filter !== undefined && api.E_CATALOG_REQUEST_FILTERS.some((f) => f === filter), `탭 버튼의 data-filter 는 E_CATALOG_REQUEST_FILTERS 중 하나여야 한다. 받은 값: ${filter}`);
    history.replaceState(null, '', `${MENU_HREF}/${filter}`);
    this.data = { ...this.data, filter: filter as ECatalogRequestFilter, offset: 0 };
    void this.load();
  }

  /** 줄 어디를 눌러도 상세가 새 탭. 파트넘버 링크(a)를 눌렀을 땐 브라우저가 이미 여니 두 번 안 연다 */
  handleOpen(e: Event, el: HTMLElement): void {
    if (hasTextSelection()) return;   // 긁어 복사(드래그)면 안 연다 — 클릭과 가른다(2026-10-30 전수)
    if ((e.target as HTMLElement).closest('a') !== null) return;
    const href = el.dataset.href;
    assert(href !== undefined, '줄은 data-href(상세 주소)를 가진다 — renderRequestRow 가 붙였다');
    window.open(href, '_blank', 'noopener');
  }

  /** 쪽 이동 — 버튼의 data-offset 으로 */
  async handlePage(_e: Event, el: HTMLElement): Promise<void> {
    const offset = Number(el.dataset.offset);
    assert(Number.isInteger(offset) && offset >= 0, '페이저 버튼은 data-offset(그 쪽의 offset)을 가진다 — renderPagerLine 이 붙였다');
    this.data = { ...this.data, offset };
    await this.load();
  }

  // ─── 메모 창(components/notes-pop — 제품 목록과 같은 꼴, 2026-10-19) ───
  /** 아이콘 → 창 열기(같은 줄이면 닫기) */
  handleNotesToggle(_e: Event, el: HTMLElement): void {
    const rowId = el.dataset.id;
    assert(rowId !== undefined, '메모 아이콘은 data-id(pending id)를 가진다 — renderNotesButton 이 붙였다');
    if (this.data.notesPop !== null && this.data.notesPop.rowId === rowId) {
      this.closeNotesPop();
      return;
    }
    const row = this.rowOf(rowId);
    this.data = { ...this.data, notesPop: notesPopAt(rowId, row.notes === null ? '' : row.notes, el) };
    this.rerenderBody();
    this._rootElement?.querySelector<HTMLTextAreaElement>('.pl-notes-pop textarea')?.focus();
  }

  /** 글 — draft 만 바꾼다. 다시 그리면 글상자 커서가 날아간다 */
  handleNotesInput(_e: Event, el: HTMLElement): void {
    const pop = this.data.notesPop;
    assert(pop !== null, '메모 글상자는 열린 창에만 있다 — renderNotesButton 이 그렸다');
    this.data = { ...this.data, notesPop: { ...pop, draft: (el as HTMLTextAreaElement).value } };
  }

  handleNotesKey(e: Event): void {
    if ((e as KeyboardEvent).key === 'Escape') this.closeNotesPop();
  }

  /** 창 안 클릭 — 줄 열기로 안 넘어가게 받기만 한다 */
  handleNotesPop(): void {
    // 일부러 비움
  }

  handleNotesClose(): void {
    this.closeNotesPop();
  }

  /** 저장 → 그 줄의 notes 만 바꿔 끼우고 닫는다. 실패면 창에 글. 빈 글은 서버가 null 로 */
  async handleNotesSave(): Promise<void> {
    const pop = this.data.notesPop;
    assert(pop !== null, '[저장] 은 열린 창에만 있다 — renderNotesButton 이 그렸다');
    this.data = { ...this.data, notesPop: { ...pop, saving: true, error: '' } };
    this.rerenderBody();
    try {
      const saved = await api.updateCatalogRequestNotes(Number(pop.rowId), pop.draft);
      const list = this.data.list;
      assert(list !== null, '메모 창은 받은 묶음의 줄에서만 열린다');
      const rows = list.rows.map((row) => String(row.pending_id) === pop.rowId ? { ...row, notes: saved.notes } : row);
      this.data = { ...this.data, list: { ...list, rows }, notesPop: null };
    } catch (e) {
      this.data = { ...this.data, notesPop: { ...pop, saving: false, error: e instanceof Error ? e.message : String(e) } };
    }
    this.rerenderBody();
  }

  // ─── 안쪽 ───
  /** 받은 묶음에서 요청 한 줄. 아이콘은 그려진 줄에만 있으니 없으면 있을 수 없는 상태 */
  private rowOf(rowId: string): api.CatalogRequestView {
    const row = this.data.list === null ? undefined : this.data.list.rows.find((candidate) => String(candidate.pending_id) === rowId);
    assert(row !== undefined, `메모 아이콘의 요청 ${rowId} 가 받은 묶음에 없다 — 아이콘은 renderRequestRow 가 그린 줄에만 있다`);
    return row;
  }

  private closeNotesPop(): void {
    this.data = { ...this.data, notesPop: null };
    this.rerenderBody();
  }

  /** [이미 있는 요청 닫기] — 묻고 돌린 뒤 보고(닫음 · product 채움 · 건너뜀)를 알리고 목록을 다시 받는다 */
  async handleCloseInCatalog(): Promise<void> {
    if (!confirm(CLOSE_IN_CATALOG_ASK)) return;
    const report = await api.closeRequestsInCatalog();
    this.data = { ...this.data, closeReport: report };   // alert 대신 결과 카드(현민 2026-10-20 "가독성 있게"). load 가 본문을 다시 그린다
    await this.load();
  }

  handleCloseReportDismiss(): void {
    this.data = { ...this.data, closeReport: null };
    this.rerenderBody();
  }

  private async load(): Promise<void> {
    const seq = ++this.loadSeq;
    this.data = { ...this.data, loading: true, error: '', notesPop: null };
    this.rerenderBody();
    try {
      if (this.data.filter === 'all') {
        const lines = await api.fetchCatalogLines(this.data.q, this.data.table, this.data.offset);
        if (seq !== this.loadSeq) return;   // 그 사이 새 불러오기가 시작됐다 — 이 응답은 버린다
        this.data = { ...this.data, lines, loading: false };
      } else {
        const list = await api.fetchCatalogRequests(this.data.filter, this.data.q, this.data.period, this.data.offset);
        if (seq !== this.loadSeq) return;
        this.data = { ...this.data, list, loading: false };
      }
    } catch (e) {
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, list: null, lines: null, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.rerenderBody();
  }

  private renderBody(): string {
    const table = this.data.filter === 'all' ? renderCatalogLineTable(this.data) : renderRequestTable(this.data);
    return `${renderRequestFilters(this.data)}${renderCloseReport(this.data.closeReport)}${table}`;
  }

  private rerenderBody(): void {
    this.refreshRegion('.cr-body', () => this.renderBody());
  }
}
